// SelectExistingUserModal.tsx
import React, { useState, useMemo, useCallback } from "react";
import { Modal, Button, Tag, Typography, App, Badge } from "antd";
import { CloseOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Table from "../../../../../../Common/Table";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import styles from "./index.module.scss";
import {
  useMembershipCreate,
  UserTableRow,
  useUsersTableGet,
} from "../../../../../../../hooks/api/users";
import { RequestData } from "@ant-design/pro-components";
import { SortOrder } from "antd/es/table/interface";
import { queryFilterParams, getSorterParams } from "../../../../../../../utils";
import { useParams, useSearchParams } from "react-router";
import { SubscriptionStatusDisplay } from "..";
import { UserStatusType } from "../../../../../../../store/auth";
import { getMessageInError } from "../../../../../../../hooks/fetch";

interface SelectExistingUserModalProps {
  open: boolean;
  onClose: () => void;
  vaadId: string;
  onSuccess?: () => void;
}

export enum UserStatusColor {
  "active" = "green",
  "pending" = "blue",
  "deactivated" = "orange",
  "banned" = "red",
  "Deleted" = "gray",
}

export function getUserStatusColor(status: UserStatusType): string {
  // @ts-ignore take a color from the list
  return UserStatusColor[status?.toLowerCase()] || "rgba(0,0,0,.15)";
}

export default function SelectExistingUserModal({
  open,
  onClose,
  vaadId,
  onSuccess,
}: SelectExistingUserModalProps): React.ReactElement {
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const { id } = useParams();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showAllSelected, setShowAllSelected] = useState(false);
  const [inviting, setInviting] = useState(false);
  const usersGet = useUsersTableGet();
  const membershipCreate = useMembershipCreate();
  const [, setSearchParams] = useSearchParams();
  const { message } = App.useApp();

  // Получаем полные данные выбранных пользователей
  const selectedUsers = useMemo(() => {
    const allUsers = usersGet.data?.data || [];
    return allUsers.filter((u) => selectedIds.has(u.id));
  }, [selectedIds, usersGet.data]);

  // Добавляем/удаляем ID из Set
  const toggleSelection = useCallback((id: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const handleRemoveSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleClearAll = () => {
    setSelectedIds(new Set());
  };

  const handleInvite = async () => {
    if (!vaadId || selectedIds.size === 0) return;

    setInviting(true);

    const results = await Promise.all(
      Array.from(selectedIds).map((userId) =>
        membershipCreate.fetch({
          user: { id: userId },
          vaad: { id: vaadId },
        }),
      ),
    );

    // fetch не бросает исключение, а возвращает объект ошибки — фильтруем их
    const failed = results.filter(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (r) => !r || (r as any)?.isAxiosError || r instanceof Error,
    );
    const succeeded = results.length - failed.length;

    // показываем реальный текст ошибки с сервера (без дублей)
    [...new Set(failed.map((err) => getMessageInError(err)))].forEach((msg) =>
      message.error(msg),
    );

    if (succeeded > 0) {
      message.success(`${succeeded} user(s) invited successfully`);
      onSuccess?.();
    }

    setInviting(false);
    setSelectedIds(new Set());
    setShowAllSelected(false);
    setSearchParams({}, { replace: true });
    onClose();
  };

  const handleClose = () => {
    setSelectedIds(new Set());
    setShowAllSelected(false);
    setSearchParams({}, { replace: true });
    onClose();
  };

  const visibleSelected = showAllSelected
    ? selectedUsers
    : selectedUsers.slice(0, 4);

  const columns = (): ProColumns<UserTableRow>[] => [
    {
      title: "Full Name",
      dataIndex: "fullName",
      key: "fullName",
      sorter: true,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ellipsis: true,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      ellipsis: true,
    },
    {
      title: "User Status",
      dataIndex: "status",
      key: "status",
      render: (_, record) => (
        <div>
          <Badge
            color={getUserStatusColor(record.status)}
            text={
              <span className="capitalize-first-letter">{record.status}</span>
            }
          />
        </div>
      ),
    },
    {
      title: "Subscription Status",
      dataIndex: "subscriptionStatus",
      key: "subscriptionStatus",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => {
        const status = record.subscriptionStatus
          ? SubscriptionStatusDisplay[
          record.subscriptionStatus as keyof typeof SubscriptionStatusDisplay
          ]
          : undefined;

        if (!status) {
          return <Badge color="default" text="-" />;
        }

        return <Badge color={status.color} text={status.text} />;
      },
    },
  ];

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number | undefined;
      current?: number | undefined;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<UserTableRow>>> => {
    const newParams = queryFilterParams({
      page: current ? `${current}` : "1",
      pageSize: pageSize ? `${pageSize}` : "10",
      ...args,
      ...getSorterParams(sorter),
    });

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });
    return usersGet
      .fetch({
        ...newParams,
        excludeVaadId: id,
        role: undefined,
        roles: newParams.role ? newParams?.role?.split(",") : undefined,
      })
      .then((response) => {
        if (response) {
          const { data, total } = response;

          return {
            data: (data || []).map((item) => ({
              ...item,
              key: item.id,
            })),
            success: true,
            total,
          };
        }

        return { data: [], success: false, total: 0 };
      });
  };

  return (
    <Modal
      title="Select existing user"
      open={open}
      onCancel={handleClose}
      width={900}
      footer={null}
      destroyOnHidden
    >
      <div className={styles.container}>
        <Table<UserTableRow>
          key={`past-${tableKey}`}
          resetKey={() => setTableKey(Date.now())}
          className="tableCustomPadding"
          headerTitle="SelectUserModal"
          request={tableRequest}
          rowSelection={{
            type: "checkbox",
            // Преобразуем Set в массив для rowSelection
            selectedRowKeys: Array.from(selectedIds),
            // Сохраняем выбор при смене страницы
            preserveSelectedRowKeys: true,
            onChange: (selectedRowKeys, selectedRows) => {
              // Получаем ID текущей страницы
              const currentPageIds = (usersGet.data?.data || []).map(
                (u) => u.id,
              );

              setSelectedIds((prev) => {
                const next = new Set(prev);

                // Удаляем все ID текущей страницы из выбранных
                currentPageIds.forEach((id) => next.delete(id));

                // Добавляем заново выбранные на текущей странице
                (selectedRowKeys as string[]).forEach((id) => next.add(id));

                return next;
              });
            },
          }}
          columns={columns()}
          search={{
            style: {
              display: "none",
            },
          }}
          options={{
            density: false,
            setting: false,
            reload: false,
            search: {
              name: "search",
              placeholder: "Search",
              style: { width: 280 },
            },
          }}
          columnsState={{
            persistenceKey: "pro-table-select-user",
            persistenceType: "localStorage",
          }}
          tableAlertRender={({ selectedRowKeys, onCleanSelected }) => {
            if (selectedIds.size === 0) return null;

            return (
              <div
                style={{
                  backgroundColor: "#E6F4FF",
                  border: "1px solid #1677FF",
                  borderRadius: 8,
                  padding: "16px 20px",
                  marginBottom: 16,
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "flex-start", gap: 12 }}
                >
                  <Typography.Text
                    strong
                    style={{
                      fontSize: 12,
                      whiteSpace: "nowrap",
                      paddingTop: 4,
                      minWidth: "fit-content",
                    }}
                  >
                    {selectedIds.size} user
                    {selectedIds.size !== 1 ? "s" : ""} selected:
                  </Typography.Text>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px 8px",
                      flex: 1,
                      lineHeight: "28px",
                    }}
                  >
                    {visibleSelected.map((user) => (
                      <Tag
                        key={user.id}
                        closable
                        onClose={() => handleRemoveSelected(user.id)}
                        style={{
                          backgroundColor: "#FFFFFF",
                          border: "1px solid #00000026",
                          borderRadius: 4,
                          padding: "1px 8px",
                          fontSize: 14,
                          color: "#000000D9",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          marginRight: 0,
                          height: 28,
                        }}
                        closeIcon={
                          <CloseOutlined
                            style={{ fontSize: 10, color: "#000000E0" }}
                          />
                        }
                      >
                        <span
                          style={{
                            maxWidth: 280,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={user.fullName}
                        >
                          {user.fullName}
                        </span>
                      </Tag>
                    ))}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 16,
                    marginTop: 12,
                  }}
                >
                  {selectedUsers.length > 4 && (
                    <Button
                      type="primary"
                      ghost
                      icon={showAllSelected ? <UpOutlined /> : <DownOutlined />}
                      onClick={() => setShowAllSelected(!showAllSelected)}
                    >
                      Show {showAllSelected ? "less" : "more"}
                    </Button>
                  )}
                  <Button
                    type="link"
                    size="small"
                    onClick={() => {
                      onCleanSelected();
                      handleClearAll();
                    }}
                    style={{ padding: 0, color: "#000000E0" }}
                  >
                    Clear all
                  </Button>
                </div>
              </div>
            );
          }}
          tableAlertOptionRender={false}
        />

        <div className={styles.footer}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleInvite}
            disabled={selectedIds.size === 0}
            loading={inviting || membershipCreate.loading}
          >
            Invite ({selectedIds.size})
          </Button>
        </div>
      </div>
    </Modal>
  );
}
